/**
 * Activity Link Utilities
 *
 * Centralized utilities for generating and parsing activity filter links.
 * Used by manage pages to link to Cut Activity with pre-applied filters.
 */

import { AppRoutes } from '@consts/routes';
import { CutFilterOptions } from '@/types/cuts';

/**
 * Entity types that can link to activity
 */
export type ActivityFilterEntity = 'company' | 'reseller' | 'location' | 'system';

/**
 * Configuration for each entity type's activity link
 */
interface EntityConfig {
    filterKey: CutFilterOptions;
    paramKey: string;
    labelPrefix: string;
    extraParams?: string[];
}

const ENTITY_CONFIG: Record<ActivityFilterEntity, EntityConfig> = {
    company: {
        filterKey: CutFilterOptions.Company,
        paramKey: 'companyId',
        labelPrefix: 'Company',
    },
    reseller: {
        filterKey: CutFilterOptions.Reseller,
        paramKey: 'resellerId',
        labelPrefix: 'Reseller',
    },
    location: {
        filterKey: CutFilterOptions.Location,
        paramKey: 'locationId',
        labelPrefix: 'Location',
    },
    system: {
        filterKey: CutFilterOptions.Station,
        paramKey: 'stationId',
        labelPrefix: 'System',
        extraParams: ['serialNum'],
    },
};

/**
 * Generate an activity URL with filter params for an entity
 */
export const createActivityLink = (
    entity: ActivityFilterEntity,
    id: string | number,
    extras?: Record<string, string>
): string => {
    const config = ENTITY_CONFIG[entity];
    const params = new URLSearchParams({
        [config.paramKey]: String(id),
        ...extras,
    });
    return `${AppRoutes.Activity}?${params.toString()}`;
};

/**
 * Navigate to activity page with filter for an entity
 */
export const navigateToActivity = (
    entity: ActivityFilterEntity,
    id: string | number,
    extras?: Record<string, string>
): void => {
    window.location.href = createActivityLink(entity, id, extras);
};

/**
 * Parse activity filter params from URL search params.
 * Returns the filters to apply and params to clean up.
 */
export const parseActivityParams = (
    searchParams: URLSearchParams
): {
    filters: Partial<Record<CutFilterOptions, Record<string, boolean>>>;
    paramsToDelete: string[];
    stationLabel?: string;
} => {
    const filters: Partial<Record<CutFilterOptions, Record<string, boolean>>> = {};
    const paramsToDelete: string[] = [];
    let stationLabel: string | undefined;

    for (const [entity, config] of Object.entries(ENTITY_CONFIG) as [ActivityFilterEntity, EntityConfig][]) {
        const paramValue = searchParams.get(config.paramKey);

        if (paramValue) {
            filters[config.filterKey] = { [paramValue]: true };
            paramsToDelete.push(config.paramKey);

            // Handle extra params (e.g., serialNum for system)
            if (config.extraParams) {
                for (const extraParam of config.extraParams) {
                    if (searchParams.has(extraParam)) {
                        paramsToDelete.push(extraParam);
                    }
                }
            }

            // Special case: generate label for station filter
            if (entity === 'system') {
                const serialNum = searchParams.get('serialNum');
                stationLabel = serialNum ? `#${paramValue} - ${serialNum}` : `${config.labelPrefix} #${paramValue}`;
            }
        }
    }

    return { filters, paramsToDelete, stationLabel };
};

/**
 * Get all entity configs (for iteration)
 */
export const getEntityConfigs = () => ENTITY_CONFIG;
